'use client';

import c from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { elementIsNotVisible } from 'utils';
import { GalleryControls } from './GalleryControls';
import GallerySlide, { GallerySlideData } from './GallerySlide';
import { GalleryThumbnailButton } from './GalleryThumbnailButton';

export type GalleryProps = {
  position: 'details' | 'fullscreen-preview';
  slides: GallerySlideData[];
  className?: string;
  hasCookieConsent: boolean;
  microcopy: {
    galleryAriaLabel: string;
    previousAriaLabel: string;
    nextAriaLabel: string;
    videoCookieOverlayMicrocopy: {
      description: string;
      buttonLabel: string;
    };
  };
  /**
   * Supported Tailwind aspect ratios: 'aspect-video' (16/9), 'aspect-square' (1/1), 'aspect-[4/3]', etc.
   * Default is 'aspect-video'.
   */
  aspectRatio?: 'aspect-video' | 'aspect-[4/3]' | 'aspect-[3/2]';
};

export const Gallery = ({
  position = 'details',
  slides,
  microcopy,
  className,
  hasCookieConsent,
  aspectRatio = 'aspect-video',
}: GalleryProps) => {
  const [currentSlide, setCurrentSlide] = useState<number>(0);
  const thumbnailContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // scroll thumbnail buttons horizontally with the scroll wheel on mobile screen sizes.
    // on desktop sizes, vertical scroll will already work
    const scrollThumbnailButtonsListener = (event) => {
      const TABLET_SCREEN_BOUNDARY = 1024;
      if (
        thumbnailContainerRef?.current &&
        window.innerWidth < TABLET_SCREEN_BOUNDARY
      ) {
        event.preventDefault();
        thumbnailContainerRef.current.scrollLeft += event.deltaY;
      }
    };

    thumbnailContainerRef.current?.addEventListener(
      'wheel',
      scrollThumbnailButtonsListener
    );

    return () => {
      thumbnailContainerRef.current?.removeEventListener(
        'wheel',
        scrollThumbnailButtonsListener
      );
    };
  }, []);

  if (slides.length === 0) {
    return null;
  }

  const handleSetCurrentSlide = (index: number) => {
    const thumbnailButton = document.getElementById(
      `gallery-thumbnail-${index}`
    );

    // scroll to the thumbnail button if it is not visible
    if (
      thumbnailButton &&
      thumbnailContainerRef?.current &&
      elementIsNotVisible(thumbnailButton, thumbnailContainerRef.current)
    ) {
      const OFFSET_FOR_SPACING = 2;
      thumbnailContainerRef.current?.scrollTo({
        top: thumbnailButton.offsetTop - OFFSET_FOR_SPACING,
        left: thumbnailButton.offsetLeft - OFFSET_FOR_SPACING,
      });
    }

    setCurrentSlide(index);
  };

  const focusSlideContent = (index: number) => {
    // focus current slide
    const slide = document.getElementById(`gallery-slide-${index}`);
    if (slide) {
      slide.tabIndex = -1;
      // slide can't be focused while it has visibility: none CSS rule, setTimeout waits one tick for the slide to be visible
      setTimeout(() => {
        slide.focus();
      }, 0);
    }
  };

  const nextSlide = () => {
    handleSetCurrentSlide(
      currentSlide === slides.length - 1 ? 0 : currentSlide + 1
    );
  };

  const previousSlide = () => {
    handleSetCurrentSlide(
      currentSlide === 0 ? slides.length - 1 : currentSlide - 1
    );
  };

  const aspectRatioThumbsMargins = () => {
    switch (aspectRatio) {
      case 'aspect-video':
        return 'mb-0';
      case 'aspect-[4/3]':
        return 'mb-0';
      case 'aspect-[3/2]':
        return 'mb-0';
      default:
        return 'mb-0';
    }
  };

  return (
    <section
      role="group"
      aria-label={microcopy.galleryAriaLabel}
      className={c(
        'bg-gray-100 relative grid w-full grid-cols-8 lg:gap-x-3 lg:p-4',
        className
      )}
    >
      {slides.length > 1 && (
        <div
          ref={thumbnailContainerRef}
          className={c(
            `${slides.length > 1 ? `bg-gray-100 light-custom-scrollbar hide-scrollbar relative order-3 col-span-8 ${aspectRatioThumbsMargins()} lg-h-auto min-h-0 overflow-auto lg:order-1 lg:col-span-1 lg:mx-0 lg:mt-0 lg:h-full lg:min-h-14 lg:pt-0` : 'hidden'}`
          )}
        >
          <ul className="flex w-full gap-3 bg-grey-100 px-4 pt-4 lg:absolute lg:w-full lg:flex-col lg:gap-1 lg:bg-white lg:px-1 lg:pl-2 lg:pt-1">
            {slides.map((slide, index) => (
              <li key={`${slide.src}-${index}`}>
                <GalleryThumbnailButton
                  id={`gallery-thumbnail-${index}`}
                  currentSlide={currentSlide}
                  setCurrentSlide={() => {
                    handleSetCurrentSlide(index);
                    focusSlideContent(index);
                  }}
                  slide={slide}
                  index={index}
                  aspectRatio={aspectRatio}
                />
              </li>
            ))}
          </ul>
        </div>
      )}
      <div
        className={c(
          `${slides.length > 1 ? 'bg-gray-100 order-3 col-span-8 flex flex-col lg:col-span-7 lg:col-start-2 lg:mx-0' : 'hidden'}`,
          aspectRatio
        )}
      >
        {slides.length > 1 && (
          <GalleryControls
            currentSlide={currentSlide}
            totalSlides={slides.length}
            nextSlide={nextSlide}
            previousSlide={previousSlide}
            previousAriaLabel={microcopy.previousAriaLabel}
            nextAriaLabel={microcopy.nextAriaLabel}
          />
        )}
      </div>
      <div
        className={c(
          `${slides.length > 1 ? 'bg-gray-100 order-1 col-span-8 flex flex-col px-4 pt-4 lg:col-span-7 lg:col-start-2 lg:mx-0 lg:mt-0 lg:p-0' : 'bg-gray-100 col-span-8 col-start-1 lg:p-0'}`,
          aspectRatio
        )}
        style={{
          background: '#f5f5f5',
        }}
      >
        <div className="bg-gray-100 flex h-full w-full overflow-hidden">
          {slides.map((slide, index) => (
            <GallerySlide
              key={`${slide.src}-${index}`}
              id={`gallery-slide-${index}`}
              isHidden={currentSlide !== index}
              style={
                index === 0 ? { marginLeft: `-${currentSlide * 100}%` } : {}
              }
              position={position}
              slide={slide}
              hasCookieConsent={hasCookieConsent}
              videoCookieOverlayMicrocopy={
                microcopy.videoCookieOverlayMicrocopy
              }
            />
          ))}
        </div>
      </div>
    </section>
  );
};
